import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Mail, Phone, Building2, UserCheck, Users, FileUp, ShieldCheck,
  CheckCircle2, AlertTriangle, Loader2, Send, Plus, Trash2,
 UserPlus } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";

const STAGE_LABELS = {
  account_created: "Account created",
  email_verification_pending: "Verify your email",
  email_verified: "Email verified",
  phone_verification_pending: "Verify your phone",
  business_profile_draft: "Business profile draft",
  business_identity_submitted: "Identity submitted",
  duplicate_check: "Duplicate check",
  duplicate_review_required: "Duplicate review required",
  representative_verification: "Representative verification",
  ownership_information: "Ownership information",
  document_collection: "Document collection",
  agency_checks: "Agency checks",
  risk_review: "Risk review",
  manual_review: "Under manual review",
  additional_information_required: "Additional information required",
  onboarding_approved: "Onboarding approved",
  onboarding_rejected: "Onboarding rejected",
  compliance_blocked: "Compliance blocked",
};

const BADGE_LABELS = {
  email_confirmed: "Email Confirmed",
  phone_confirmed: "Phone Confirmed",
  business_registered: "Business Registered",
  representative_verified: "Representative Verified",
  address_verified: "Address Verified",
  ownership_reviewed: "Ownership Reviewed",
  license_verified: "License Verified",
  government_registered: "Government Registered",
  sanctions_screened: "Sanctions Screened",
  fully_verified_business: "Fully Verified Business",
};

const RISK_COLORS = {
  low: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  prohibited: "bg-red-100 text-red-700",
};

function Card({ title, icon: Icon, children, done, testid }) {
  return (
    <section data-testid={testid}
      className="border border-slate-200 bg-white rounded-xl p-5 lg:p-6">
      <div className="flex items-center gap-2.5 mb-4">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${done ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
          {done ? <CheckCircle2 size={18} /> : <Icon size={18} />}
        </div>
        <h3 className="font-heading font-bold text-[#0A0F1C]">{title}</h3>
      </div>
      {children}
    </section>
  );
}

const inputCls =
  "mt-1 w-full h-10 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none text-sm";

export default function Onboarding() {
  const [status, setStatus] = useState(null);
  const [biz, setBiz] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
      if (data.business_id) {
        const [b, d] = await Promise.all([
          api.get(`/onboarding/business/${data.business_id}`),
          api.get(`/onboarding/business/${data.business_id}/documents`),
        ]);
        setBiz(b.data);
        setDocs(d.data.documents || []);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(""); }
  };

  if (loading) {
    return <AppShell title="Verification"><div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div></AppShell>;
  }

  const s = status || {};
  const comps = s.components || {};

  return (
    <AppShell title="Business Verification" subtitle="Complete verification to unlock the directory, AI matches and deal-making.">
      <div className="grid gap-5 max-w-4xl">
        {/* Status header */}
        <div data-testid="onboarding-status-header" className="border border-slate-200 bg-white rounded-xl p-5 lg:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Onboarding stage</div>
              <div data-testid="onboarding-stage" className="font-heading text-lg font-bold text-[#0A0F1C]">
                {STAGE_LABELS[s.onboarding_stage] || s.onboarding_stage}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${RISK_COLORS[s.risk_score] || "bg-slate-100 text-slate-600"}`}>
                Risk: {s.risk_score || "low"}
              </span>
              <span data-testid="verification-state-badge" className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.access?.fully_verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                {s.verification_state}
              </span>
            </div>
          </div>
          {s.badges?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="onboarding-badges">
              {s.badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 text-[#2563EB] text-xs font-medium">
                  <ShieldCheck size={12} /> {BADGE_LABELS[b] || b}
                </span>
              ))}
            </div>
          )}
          {s.duplicate_review && (
            <div className="mt-4 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-md p-3" data-testid="duplicate-review-notice">
              <AlertTriangle size={16} className="mt-0.5" />
              This business matched an existing record and is under duplicate review. A compliance officer must approve it.
            </div>
          )}
        </div>

        {/* Email */}
        <Card title="Email verification" icon={Mail} done={s.email_verified} testid="onboarding-email-card">
          {s.email_verified ? (
            <p className="text-sm text-slate-500">Your email address is confirmed.</p>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-600 flex-1">Confirm your email to continue. We'll send a verification link.</p>
              <button data-testid="send-email-verification-btn" disabled={busy === "email"}
                onClick={() => run("email", async () => { const { data } = await api.post("/onboarding/email/send-verification"); if (data?.preview_token) { window.location.assign(`/verify-email?token=${data.preview_token}`); return; } toast.success("Verification email sent. Check your inbox."); })}
                className="h-10 px-4 rounded-md bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] flex items-center gap-2 disabled:opacity-60">
                {busy === "email" ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Send link
              </button>
            </div>
          )}
        </Card>

        {/* Phone */}
        {s.email_verified && <PhoneCard status={s} onDone={load} />}

        {/* Business */}
        {s.email_verified && (
          !s.has_business
            ? <BusinessForm onDone={load} />
            : <>
                <Card title="Business profile" icon={Building2} done testid="onboarding-business-card">
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Field label="Legal name" value={biz?.legal_business_name} />
                    <Field label="Entity type" value={biz?.entity_type} />
                    <Field label="Jurisdiction" value={biz?.jurisdiction} />
                    <Field label="Industry" value={biz?.industry} />
                    <Field label="Status" value={biz?.status} />
                  </div>
                </Card>
                <RepresentativeCard biz={biz} comps={comps} onDone={load} />
                <OwnershipCard biz={biz} onDone={load} />
                <DocumentsCard biz={biz} docs={docs} onDone={load} />
                <SubmitCard biz={biz} status={s} onDone={load} />
                <InviteCard biz={biz} />
              </>
        )}
      </div>
    </AppShell>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <span className="text-slate-400">{label}: </span>
      <span className="text-[#0A0F1C] font-medium">{value || "—"}</span>
    </div>
  );
}

function PhoneCard({ status, onDone }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  if (status.phone_verified) {
    return <Card title="Phone verification" icon={Phone} done testid="onboarding-phone-card"><p className="text-sm text-slate-500">Phone number confirmed.</p></Card>;
  }
  return (
    <Card title="Phone verification" icon={Phone} testid="onboarding-phone-card">
      <div className="space-y-3">
        <div className="flex gap-2">
          <input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 415 555 1212" className={inputCls} />
          <button data-testid="send-phone-code-btn" disabled={busy}
            onClick={async () => { setBusy(true); try { const { data } = await api.post("/onboarding/phone/send-code", { phone }); setSent(true); toast.success(data.preview_code ? `Preview code: ${data.preview_code}` : "Code sent"); if (data.preview_code) setCode(data.preview_code); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }}
            className="h-10 px-4 rounded-md bg-slate-800 text-white text-sm font-medium whitespace-nowrap">Send code</button>
        </div>
        {sent && (
          <div className="flex gap-2">
            <input data-testid="phone-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className={inputCls} />
            <button data-testid="verify-phone-btn"
              onClick={async () => { try { await api.post("/onboarding/phone/verify", { code }); toast.success("Phone verified"); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}
              className="h-10 px-4 rounded-md bg-[#2563EB] text-white text-sm font-medium">Verify</button>
          </div>
        )}
        <p className="text-xs text-slate-400">SMS provider is architecture-ready; in preview the code is shown for testing and never works in production.</p>
      </div>
    </Card>
  );
}

function BusinessForm({ onDone }) {
  const [f, setF] = useState({ legal_business_name: "", entity_type: "llc", country_of_formation: "US", jurisdiction: "", registration_number: "", industry: "", role: "seller", website: "", physical_address: "" });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card title="Create your business profile" icon={Building2} testid="onboarding-business-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Legal business name*" testid="biz-legal-name" value={f.legal_business_name} onChange={up("legal_business_name")} />
        <Input label="Registration number" testid="biz-reg-number" value={f.registration_number} onChange={up("registration_number")} />
        <div>
          <label className="text-xs font-medium text-slate-600">Entity type</label>
          <select data-testid="biz-entity-type" value={f.entity_type} onChange={up("entity_type")} className={inputCls}>
            {["llc", "corporation", "s_corp", "c_corp", "partnership", "sole_proprietor", "nonprofit", "trust", "holding_company", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <Input label="Country of formation" testid="biz-country" value={f.country_of_formation} onChange={up("country_of_formation")} />
        <Input label="State / jurisdiction" testid="biz-jurisdiction" value={f.jurisdiction} onChange={up("jurisdiction")} />
        <Input label="Industry" testid="biz-industry" value={f.industry} onChange={up("industry")} placeholder="e.g. logistics, construction" />
        <div>
          <label className="text-xs font-medium text-slate-600">Platform role</label>
          <select data-testid="biz-role" value={f.role} onChange={up("role")} className={inputCls}>
            {["buyer", "seller", "supplier", "contractor", "investor", "lender", "provider"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <Input label="Website" testid="biz-website" value={f.website} onChange={up("website")} placeholder="https://" />
        <div className="sm:col-span-2"><Input label="Physical address" testid="biz-address" value={f.physical_address} onChange={up("physical_address")} /></div>
      </div>
      <button data-testid="create-business-btn" disabled={busy || !f.legal_business_name}
        onClick={async () => { setBusy(true); try { const { data } = await api.post("/onboarding/business", f); toast.success(data.duplicate_review ? "Submitted — duplicate review required" : "Business profile created"); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }}
        className="mt-4 h-10 px-5 rounded-md bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] disabled:opacity-60">
        {busy ? "Creating…" : "Create business"}
      </button>
    </Card>
  );
}

function Input({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input data-testid={testid} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} />
    </div>
  );
}

function RepresentativeCard({ biz, comps, onDone }) {
  const [f, setF] = useState({ legal_name: "", title: "", relationship: "owner", signing_authority: true });
  const [busy, setBusy] = useState(false);
  const rep = (biz?.representatives || [])[0];
  const done = rep?.status === "representative_verified";
  return (
    <Card title="Authorized representative" icon={UserCheck} done={done} testid="onboarding-representative-card">
      {rep && <p className="text-sm text-slate-500 mb-3">Representative <b>{rep.legal_name}</b> — status: <b data-testid="rep-status">{rep.status}</b></p>}
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Full legal name" testid="rep-name" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} />
        <Input label="Title" testid="rep-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600 mt-3">
        <input type="checkbox" data-testid="rep-signing-authority" checked={f.signing_authority} onChange={(e) => setF({ ...f, signing_authority: e.target.checked })} />
        I have signing authority for this business
      </label>
      <button data-testid="submit-representative-btn" disabled={busy || !f.legal_name || !f.title}
        onClick={async () => { setBusy(true); try { await api.post(`/onboarding/business/${biz.business_id}/representative`, f); toast.success("Representative submitted for review"); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }}
        className="mt-3 h-10 px-5 rounded-md bg-slate-800 text-white text-sm font-medium disabled:opacity-60">Submit representative</button>
    </Card>
  );
}

function OwnershipCard({ biz, onDone }) {
  const [owners, setOwners] = useState([{ legal_name: "", ownership_percentage: 100, residence_country: "US", controlling_person: true, identity_status: "pending" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setOwners(owners.map((o, idx) => idx === i ? { ...o, [k]: v } : o));
  return (
    <Card title="Beneficial ownership & control" icon={Users} testid="onboarding-ownership-card">
      <div className="space-y-3">
        {owners.map((o, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_120px_100px_auto] gap-2 items-end">
            <Input label={i === 0 ? "Owner legal name" : ""} testid={`owner-name-${i}`} value={o.legal_name} onChange={(e) => set(i, "legal_name", e.target.value)} />
            <Input label={i === 0 ? "Ownership %" : ""} testid={`owner-pct-${i}`} value={o.ownership_percentage} onChange={(e) => set(i, "ownership_percentage", Number(e.target.value))} />
            <label className="flex items-center gap-1.5 text-xs text-slate-600 h-10">
              <input type="checkbox" data-testid={`owner-control-${i}`} checked={o.controlling_person} onChange={(e) => set(i, "controlling_person", e.target.checked)} /> Control
            </label>
            {owners.length > 1 && <button onClick={() => setOwners(owners.filter((_, idx) => idx !== i))} className="h-10 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button data-testid="add-owner-btn" onClick={() => setOwners([...owners, { legal_name: "", ownership_percentage: 0, residence_country: "US", controlling_person: false, identity_status: "pending" }])}
          className="h-9 px-3 rounded-md border border-slate-300 text-sm text-slate-600 flex items-center gap-1.5"><Plus size={14} /> Add owner</button>
        <button data-testid="save-owners-btn" disabled={busy}
          onClick={async () => { setBusy(true); try { const { data } = await api.post(`/onboarding/business/${biz.business_id}/beneficial-owners`, { owners }); toast.success(data.flags?.length ? `Saved with flags: ${data.flags.join(", ")}` : "Ownership saved"); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }}
          className="h-9 px-4 rounded-md bg-slate-800 text-white text-sm font-medium disabled:opacity-60">Save ownership</button>
      </div>
    </Card>
  );
}

function DocumentsCard({ biz, docs, onDone }) {
  const [docType, setDocType] = useState("articles_of_incorporation");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try { await api.post(`/onboarding/business/${biz.business_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded securely"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Business documents" icon={FileUp} testid="onboarding-documents-card">
      <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className="h-10 px-3 rounded-md border border-slate-300 text-sm">
          {["articles_of_incorporation", "certificate_of_good_standing", "ein_document", "business_license", "insurance_certificate", "ownership_records", "import_export_docs"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
        <label className="h-10 px-4 rounded-md bg-[#2563EB] text-white text-sm font-medium flex items-center gap-2 cursor-pointer">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} Upload
          <input type="file" data-testid="doc-upload-input" className="hidden" onChange={(e) => upload(e.target.files?.[0])} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" />
        </label>
        <span className="text-xs text-slate-400">Private storage · SHA-256 hashed · max 15MB</span>
      </div>
      {docs.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="documents-list">
          {docs.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="text-[#0A0F1C]">{(d.doc_type || "").replace(/_/g, " ")} <span className="text-slate-400">v{d.version}</span></span>
              <span className="text-xs text-slate-400 truncate max-w-[45%]">{d.original_filename}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SubmitCard({ biz, status, onDone }) {
  const [busy, setBusy] = useState(false);
  const submitted = ["manual_review", "compliance_blocked"].includes(biz?.status);
  return (
    <Card title="Submit for verification" icon={ShieldCheck} testid="onboarding-submit-card">
      <p className="text-sm text-slate-600 mb-3">
        Once your representative, ownership and documents are in place, submit for compliance review. Stubbed agency checks always require a human review — documents alone never grant a Verified badge.
      </p>
      {submitted
        ? <div className="text-sm font-medium text-amber-700 bg-amber-50 rounded-md p-3" data-testid="submitted-notice">Submitted — status: {biz.status}</div>
        : <button data-testid="submit-business-btn" disabled={busy}
            onClick={async () => { setBusy(true); try { const { data } = await api.post(`/onboarding/business/${biz.business_id}/submit`); toast.success(`Submitted — ${data.status} (risk: ${data.risk_score})`); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }}
            className="h-10 px-5 rounded-md bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9A3412] disabled:opacity-60">
            {busy ? "Submitting…" : "Submit for verification"}
          </button>}
    </Card>
  );
}

function InviteCard({ biz }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/onboarding/business/${biz.business_id}/invite`, { email, role });
      toast.success("Invitation sent");
      setEmail("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card title="Invite a team member" icon={UserPlus} testid="onboarding-invite-card">
      <p className="text-sm text-slate-500 mb-3">Authorized representatives can invite colleagues. They must accept with this exact, verified email.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@company.com" className="h-10 flex-1 rounded-md border border-slate-300 px-3 text-sm" />
        <select data-testid="invite-role-select" value={role} onChange={(e) => setRole(e.target.value)}
          className="h-10 rounded-md border border-slate-300 px-3 text-sm">
          <option value="member">Member</option>
          <option value="viewer">Viewer</option>
        </select>
        <button data-testid="invite-send-btn" disabled={busy || !email} onClick={send}
          className="h-10 px-4 rounded-md bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Send invite
        </button>
      </div>
    </Card>
  );
}
